import { inject, Injectable } from '@angular/core';
import { Platform } from '@ionic/angular/standalone';
import { firstValueFrom, ReplaySubject } from 'rxjs';

import {
  Purchases,
  LOG_LEVEL,
  PurchasesStoreProduct,
} from '@revenuecat/purchases-capacitor';
import { Capacitor } from '@capacitor/core';

import { AuthService } from '@app/auth.service';
import { AnalyticsService } from '@app/analytics.service';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Membership } from './models/membership.codec';
import { environment } from 'src/environments/environment';

export const InAppPurchaseCode = {
  ONE_TIME_MEMBERSHIP: 'com.live.tractorpal2.fullmember',
  SUBSCRIPTION_MEMBERSHIP: 'com.live.tractorpal2.fullmember.subscription',
} as const;


export interface IPurchasesService {
  initialize(userId: string): Promise<void>;
  restorePurchases(): Promise<void>;
  purchase(product: PurchasesStoreProduct): Promise<void>;
}

type OwnedEntitlement = (typeof InAppPurchaseCode)[keyof typeof InAppPurchaseCode]

/**
 * Remove the base plan identifier from the product identifier for android
 * @param identifier - The product identifier
 * @returns The cleaned identifier
 */
export const getCleanedIdentifier = (identifier: string): OwnedEntitlement => {
  const cleanedIdentifier = identifier.split(':')[0];
  if (cleanedIdentifier === InAppPurchaseCode.SUBSCRIPTION_MEMBERSHIP) {
    return InAppPurchaseCode.SUBSCRIPTION_MEMBERSHIP;
  } else if (cleanedIdentifier === InAppPurchaseCode.ONE_TIME_MEMBERSHIP) {
    return InAppPurchaseCode.ONE_TIME_MEMBERSHIP;
  }
  throw new Error('Invalid identifier');
}

const getApiKeyForPlatform = () => {
  if (Capacitor.getPlatform() === 'ios') {
    console.log('[purchases.service] using ios api key');
    return 'appl_vNKSJWIESOjBkhEkwczdvMusaHG';
  } else if (Capacitor.getPlatform() === 'android') {
    console.log('[purchases.service] using android api key');
    return 'goog_odjAlRabrtZeyAIHEEwhGnqbcmB';
  }
  throw new Error('Unsupported platform');
};

@Injectable({
  providedIn: 'root',
})
export class PurchasesService implements IPurchasesService {
  private readonly platform = inject(Platform);
  private readonly authService = inject(AuthService);
  private readonly functions = inject(Functions);
  private readonly analyticsService = inject(AnalyticsService);

  private productsSubject = new ReplaySubject<PurchasesStoreProduct[]>(1);
  public readonly products$ = this.productsSubject.asObservable();

  /**
   * Initialize the purchases service
   */
  public async initialize() {
    console.log('[PurchasesService.initialize]');

    await this.platform.ready();

    const uid = await firstValueFrom(this.authService.uid$);

    if (!environment.isWebVersion) {
      await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });
      await Purchases.configure({
        apiKey: getApiKeyForPlatform(),
        appUserID: uid,
      });
    }

    await this.restorePurchases();
  }

  /**
   * Restore purchases
   * @returns The membership
   */
  public async restorePurchases(): Promise<void> {
    try {
      if (!environment.isWebVersion) {
        await Purchases.restorePurchases();
        await this.refreshProducts();
        this.analyticsService.trackEvent('restore_purchases', {
          wasSuccessful: true,
        });
      } else {
        console.warn('[PurchasesService.restorePurchases] Restoring purchases on web. Cannot check for purchases on web.');
        this.analyticsService.trackEvent('restore_purchases', {
          wasSuccessful: true,
          reason: 'Cannot check for purchases on web.',
        });
      }
      await this.refreshMembership();
    } catch (error) {
      this.analyticsService.trackEvent('restore_purchases', {
        wasSuccessful: false,
        reason: error instanceof Error ? error.message : 'Unknown error',
      });
      throw error;
    }
  }

  /**
   * Purchase a product
   * @param product - The product to purchase
   */
  public async purchase(product: PurchasesStoreProduct): Promise<void> {
    try {
      await Purchases.purchaseStoreProduct({
        product,
      });
      this.analyticsService.trackEvent('purchase', {
        title: product.title,
        productId: product.identifier,
        price: product.price,
        wasSuccessful: true,
      });
    } catch (error) {
      this.analyticsService.trackEvent('purchase', {
        title: product.title,
        productId: product.identifier,
        price: product.price,
        wasSuccessful: false,
        reason: error instanceof Error ? error.message : 'Unknown error',
      });
      throw error;
    }
  }

  /**
   * Refresh the membership from the server
   * @returns The membership
   */
  public async refreshMembership(): Promise<Membership> {
    console.log('[SubscriptionPage] Refreshing membership from server...');

    // Call the refreshMembership Cloud Function
    const refreshMembership = httpsCallable<void, Membership>(
      this.functions,
      'refreshMembership'
    );

    const result = await refreshMembership();
    console.log('[SubscriptionPage] Membership refreshed:', result.data);
    return result.data;
  }

  /**
   * Refresh the products from the server
   * @returns The products
   */
  private async refreshProducts() {
    const { products } = await Purchases.getProducts({
      productIdentifiers: [
        InAppPurchaseCode.SUBSCRIPTION_MEMBERSHIP,
        InAppPurchaseCode.ONE_TIME_MEMBERSHIP,
      ],
    });

    this.productsSubject.next(
        products,
    );
  }
}
